<script setup>
import 'devextreme-vue/text-area'
import { ref, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxSplitter, DxItem } from 'devextreme-vue/splitter'

import * as dataGrid from '@/composables/data-grid'

import { usePrint } from '@/composables/print'
import { useNotify } from '@/composables/notify'
import { useColumns } from '@/composables/order/supplier-order-grid'
import { useDialogTitle } from '@/composables/order/supplier-order-grid'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'
import { useOrderStore } from '@/stores/order.store'

import { useDocumentIdentifiers } from '@/composables/document'
import { useMultiSelectFilter, useMultiSelectEditorOptions } from '@/composables/data-grid'
import { useAttachment } from '@/composables/registry/service-attachment-grid'

import AttachmentIconList from '@/components/shared/AttachmentIconList.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'
import MultiSelectFilter from '@/components/shared/MultiSelectFilter.vue'
import PrintDialog from '@/components/shared/PrintDialog.vue'
import SupplierDivisionDropDownBox from '@/components/shared/SupplierDivisionDropDownBox.vue'
import SupplierDropDownBox from '@/components/shared/SupplierDropDownBox.vue'
import SupplierOrderRowGrid from '@/components/order/SupplierOrderRowGrid.vue'

import { useEntityLock } from '@/services/entity-lock.service'

const props = defineProps({
  selectMode: {
    type: Boolean,
    default: false,
  }
})

const gridId = 'supplier-orders-grid'
const mainSplitterId = 'supplier-orders-grid-main-splitter'
const formInstance = ref(null)

const { route, router } = dataGrid.useGridRouter()
const i18n = getI18n()
const editors = {}
const registryStore = useRegistryStore()

const portalStore = usePortalStore()
const orderStore = useOrderStore()
const webSiteStore = useWebSiteStore()
const {
  gridRef,
  refreshGridAsync,
  focusGrid,
  onFocusedRowChangedDebounced: onFocusedRowChanged,
} = dataGrid.useGridRef()

const currentSupplierId = ref(null)
const currentSupplierBrandId = ref(null)
const selectedDocument = ref(null)
const print = usePrint()

let firstEditLoad = false
let defaultToolbar = null

const { notifySuccess, notifyError } = useNotify()
const { getAttachmentEntities } = useAttachment()
const { onDocumentInitNewRowAsync, onDocumentEditorPreparing, getDocumentDetailsAsync } = useDocumentIdentifiers()
const { applyFilter, setMultiSelectFilterRef, clearMultiSelectFilter } = useMultiSelectFilter()

defineExpose({ focusedRow: dataGrid.focusedRow })

watch(() => webSiteStore.userDataFilter, () => gridRef.value.instance.refresh())

const entityLock = useEntityLock({
  baseLockUrl: orderStore.api.getAbsoluteUrl() + orderStore.api.supplierOrdersUrl,
  modelName: 'SupplierOrder',
  gridRef,
})

const rowButtons = entityLock.getEntityLockButtons()

const columns = useColumns(editors, currentSupplierId, currentSupplierBrandId)

const editing = dataGrid.useDefaultEditing(useDialogTitle().dialogTitle, {
  colCount: 4,
  items: [
    {
      itemType: 'group',
      caption: i18n.t('common.supplierOrderData'),
      colCount: 4,
      items: [
        { dataField: 'date', colSpan: 2 },
        { dataField: 'series', editorOptions: { readOnly: true } },
        { dataField: 'number_mnemonic', editorOptions: { readOnly: true } },
      ]
    },
    {
      itemType: 'tabbed',
      colSpan: 3,
      tabs: [
        {
          title: i18n.t('common.mainInfo'),
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.mainInfo'),
              captionTemplate: 'main-info-caption-template',
              colSpan: 3,
              colCount: 3,
              items: [
                { dataField: 'supplier_ref' },
                { dataField: 'supplier_division_ref' },
                { dataField: 'supplier_brand_ref', editorOptions: { readOnly: true } },
                { dataField: 'supplier_order_reason_ref' },
                { dataField: 'urgency_type_ref' },
                { dataField: 'status', editorOptions: { readOnly: true } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.destination'),
              items: [
                { dataField: 'deposit' },
              ]
            },
          ]
        },
        {
          title: i18n.t('common.orderData'),
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.orderData'),
              colSpan: 3,
              colCount: 3,
              items: [
                { dataField: 'payment_type_ref' },
                { dataField: 'discount', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions() } },
                { itemType: 'empty' },
                { dataField: 'currency_ref' },
                { dataField: 'exchange_rate', editorOptions: { readOnly: true } },
                { dataField: 'exchange_rate_date', editorOptions: { readOnly: true } },
                { dataField: 'requested_delivery_date', editorType: 'dxDateBox' },
                {
                  dataField: 'notes',
                  editorType: 'dxTextArea',
                  colSpan: 2,
                  editorOptions: { height: '4.5rem' }
                },
              ]
            },
          ]
        }
      ]
    },
  ],
  onInitialized: (e) => { formInstance.value = e.component },
}, {
  allowAdding: !props.selectMode,
  allowUpdating: !props.selectMode,
  allowDeleting: !props.selectMode,
  onInitializedCustom: (e) => entityLock.readOnlyToolbarItems(e),
  entityLock: entityLock,
})

const onInitNewRow = async(e) => {
  e.data.date = new Date()
  e.data.series = e.data.date.getFullYear().toString()
  e.data.status = 0

  if (registryStore.companyData.order_auto_insert) {
    if (registryStore.companyData.default_order_supplier_ref) {
      e.data.supplier_ref = registryStore.companyData.default_order_supplier_ref
      currentSupplierId.value = e.data.supplier_ref

      const supplier = registryStore.suppliers.find(i => i.id === registryStore.companyData.default_order_supplier_ref)
      e.data.supplier_brand_ref = supplier?.supplier_brand_ref || registryStore.getDefaultSupplierBrand()?.id
      currentSupplierBrandId.value = e.data.supplier_brand_ref

      if (registryStore.companyData.default_order_supplier_division_ref) {
        e.data.supplier_division_ref = registryStore.companyData.default_order_supplier_division_ref
        onDivisionUpdate(e.data.supplier_division_ref, e.data)
      }
    }
    if (registryStore.companyData.default_order_supplier_order_reason_ref) {
      e.data.supplier_order_reason_ref = registryStore.companyData.default_order_supplier_order_reason_ref
    }
    if (registryStore.companyData.default_order_payment_type_ref) {
      e.data.payment_type_ref = registryStore.companyData.default_order_payment_type_ref
    }
  }

  if (registryStore.companyData.deposit) {
    e.data.deposit = registryStore.companyData.deposit
  }
  if (registryStore.companyData.default_currency_ref) {
    e.data.currency_ref = registryStore.companyData.default_currency_ref

    const currentCurrency = webSiteStore.currencies.find((currency) => currency.id === registryStore.companyData.default_currency_ref)
    if (currentCurrency) {
      e.data.exchange_rate = currentCurrency.exchange_rate
      e.data.exchange_rate_date = currentCurrency.exchange_rate_date
    }
  }

  e.promise = (async() => { await onDocumentInitNewRowAsync(e, orderStore.api.getSupplierOrderNextIdByDataAsync) })()
}

const onEditingStart = (e) => {
  firstEditLoad = true
  if (e.data?.supplier_ref) {
    currentSupplierId.value = e.data.supplier_ref
    const supplier = registryStore.suppliers.find(i => i.id === e.data.supplier_ref)
    currentSupplierBrandId.value = supplier?.supplier_brand_ref
  }
  if (e.data?.supplier_division_ref) {
    onDivisionUpdate(e.data.supplier_division_ref, e.data)
  }
}

const onDivisionUpdate = (divisionId, data) => {
  const division = registryStore.supplierDivisions.find(i => i.id === divisionId)
  if (division.brand_behavior === registryStore.supplierDivisionBrandBehavior.DIV_DIV || division.brand_behavior === registryStore.supplierDivisionBrandBehavior.DIV_SUP) {
    data.supplier_brand_ref = division.supplier_brand_ref
    currentSupplierBrandId.value = division.supplier_brand_ref
  }
}

const onEditorPreparing = (e) => {
  if (firstEditLoad && e.row?.data?.id) {
    firstEditLoad = false
    e.promise = getDocumentDetailsAsync(e.row?.data?.id, orderStore.api.getSupplierOrderDetailsAsync, e.row.data, { onNotFound: () => e.component?.cancelEditData() }).then(res => {
      e.row.data = res.value
      formInstance.value?.repaint()
    })
  }

  if (e.parentType === 'filterRow' && e.dataField === 'status') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: orderStore.getLocalizedOrderStatuses(),
      field: e.dataField,
    })
  }

  if (e.parentType === 'filterRow' && e.dataField === 'urgency_type_ref') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: webSiteStore.getLocalizedUrgencyTypes(),
      field: e.dataField,
    })
  }

  if (e.parentType === 'dataRow' && e.dataField === 'supplier_brand_ref') {
    e.editorOptions.disabled = !!e.row.data?.id || (typeof e.row.data?.supplier_ref !== 'number')
  }
  if (e.parentType === 'dataRow' && e.dataField === 'order_reason_ref') {
    e.editorOptions.disabled = !!e.row.data?.id || (typeof e.row.data?.supplier_brand_ref !== 'number')
  }
  onDocumentEditorPreparing(e, editors, orderStore.api.getSupplierOrderNextIdByDataAsync)
}

let transmitOrdersToolbarButton = null
const selectedRows = ref([])

const onTransmitOrdersButtonClick = async() => {
  const data = { ids: selectedRows.value.filter(i => i.status === 0).map(row => row.id) }
  try {
    await orderStore.api.transmitSupplierOrdersAsync(data)
    notifySuccess(i18n.t('common.transmissionSuccess'))
    selectedRows.value = []
    gridRef.value.instance.clearSelection()
    await gridRef.value.instance.refresh()
  } catch (e) {
    notifyError(i18n.t('common.transmissionError'))
    console.error(e)
  }
}

const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift(
    {
      widget: 'dxButton',
      location: 'after',
      options: {
        icon: 'mdi mdi-access-point',
        disabled: true,
        text: `${i18n.t('common.transmit')}`,
        onClick: onTransmitOrdersButtonClick,
        onInitialized: (e) => { transmitOrdersToolbarButton = e.component }
      }
    },
    print.printToolbarWidget({ onClick: async() => {
      try {
        const filename = selectedRows.value.length === 1 ? i18n.t('common.order') + '.pdf' : i18n.t('common.order', 2) + '.zip'
        await orderStore.api.printSupplierOrdersAsync(filename, {filename: filename, ids: selectedRows.value.map(i => i.id), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone}, { skipErrorMessage: (e) => (e.response?.data?.warnings) })
      } catch (err) {
        handleWarningMessages(err)
      }
    }}),
    print.printOptionsToolbarWidget()
  )

  defaultToolbar = dataGrid.useDefaultToolbar(e, {
    allowBatchDelete: true,
    addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.supplierOrder')}` }
  })

  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
  print.printToolbarButton.value.option('disabled', e.selectedRowsData.length === 0)
  print.printOptionsToolbarButton.value.option('disabled', e.selectedRowsData.length === 0)

  const allowedTransmissionBrandIds = registryStore.supplierBrands.filter(i => i.is_transmission_enabled).map(i => i.id)
  transmitOrdersToolbarButton.option('disabled',
    e.selectedRowsData.length === 0 ||
    e.selectedRowsData.some(i => i.status !== 0) ||
    !e.selectedRowsData.every(i => allowedTransmissionBrandIds.some(brandId => brandId === i.supplier_brand_ref))
  )
  defaultToolbar?.batchDeleteToolbar?.option('disabled', e.selectedRowsData.length === 0)
}

const onFocusedRowChangedDebounced = async(e) => onFocusedRowChanged(e, selectedDocument)

const onSplitterResize = (e, splitterId) => {
  const totalSize = e.component.option('orientation') === 'vertical'
    ? e.component.element().offsetHeight
    : e.component.element().offsetWidth

  const sizes = e.component.option('items').map(item => {
    const px = parseFloat(item.size)
    const percentage = ((px / totalSize) * 100).toFixed(2) + '%'
    return { size: percentage }
  })
  webSiteStore.setSplitterState(splitterId, sizes)

  if (splitterId === mainSplitterId && selectedDocument.value && gridRef.value?.instance) {
    const gridInstance = gridRef.value.instance

    gridInstance.updateDimensions()
    gridInstance.repaint()

    const rowIndex = gridInstance.getRowIndexByKey(selectedDocument.value.id)
    if (rowIndex >= 0) {
      const rowHeight = gridInstance.option('rowHeight') || 37
      const scrollTop = rowIndex * rowHeight

      const scrollable = gridInstance.getScrollable()
      if (scrollable) {
        const currentScroll = scrollable.scrollOffset()
        scrollable.scrollTo({ x: currentScroll.left, y: scrollTop })
        gridInstance.repaint()
      }
    }
  }
}

const restoreSplitterSize = (e, splitterId) => {
  const saved = webSiteStore.splitterStates[splitterId]
  if (saved) {
    try {
      saved.forEach((item, index) => {
        e.component.option(`items[${index}].size`, item.size)
      })
    } catch (err) {
      console.warn(`Splitter "${splitterId}": data error.`)
    }
  }
}

const onRowRemoving = (e) => {
  selectedDocument.value = null
}

const { repaintRow } = dataGrid.useUpdateMasterDetail()

const onDetailContentReady = (e) => {
  gridRef.value.instance.updateDimensions()
}

const onRowSaved = (parentId, parentRowKey) => {
  repaintRow(gridRef, orderStore.api.getSupplierOrdersAsync, parentId, parentId)
}

const batchDeleteAsync = async(selectedIds) => {
  await orderStore.api.batchDeleteSupplierOrdersAsync(selectedIds)
  selectedDocument.value = null
}

const onSaving = (e) => {
  if (e.changes?.[0]?.type === 'remove') { selectedDocument.value = null }
}

const onSaved = async(e) => { await refreshGridAsync() }

const onClearFilter = () => {
  clearMultiSelectFilter()
  router.replace()
}

const getFilterDataSource = (field) => {
  const buildFilterOptions = (items) => [{
    items: [...items]
  }]

  switch (field) {
    case 'status':
      return buildFilterOptions(orderStore.getLocalizedOrderStatuses())
    case 'urgency_type_ref':
      return buildFilterOptions(webSiteStore.getLocalizedUrgencyTypes())
    default:
      return []
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataGrid.useODataSource(orderStore.api.getAbsoluteUrl() + orderStore.api.supplierOrdersUrl),
  columns: columns,
  editing: editing,
  selection: dataGrid.useDefaultGridSelection(),
  onInitNewRow: onInitNewRow,
  onEditingStart: onEditingStart,
  onEditorPreparing: onEditorPreparing,
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: onSelectionChanged,
  onFocusedRowChangedDebounced: onFocusedRowChangedDebounced,
  onClearFilter: onClearFilter,
  onSaving: onSaving,
  onSaved: onSaved,
  onRowRemoving: onRowRemoving,
  focusGrid: focusGrid,
  splittedGrid: true,
  routerFilter: {
    fields: [{series: 'series'}, {number: 'number_mnemonic'}],
    getRoute: () => route,
    router: router
  },
})

</script>
<template>
  <div>
    <print-dialog
      v-model:visible="print.printDialogVisible.value"
      :selected-rows="selectedRows"
      :entity="portalStore.printEntities.SUPPLIER_ORDER"
      :print-function="orderStore.api.printSupplierOrdersAsync"
      :print-preview-function="(options) => orderStore.api.printPreviewSupplierOrderAsync(options)"
    />

    <dx-splitter
      orientation="vertical"
      height="90vh"
      @resize-end="e => onSplitterResize(e, mainSplitterId)"
      @item-collapsed="e => onSplitterResize(e, mainSplitterId)"
      @initialized="e => restoreSplitterSize(e, mainSplitterId)"
    >
      <dx-item
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <dx-data-grid
          ref="gridRef"
          v-bind="gridOptions"
        >
          <template #attachmentsCellTemplate="{ data: cellInfo }">
            <attachment-icon-list
              :data="cellInfo.data"
              document-name="common.supplierOrder"
              :has-attachments="cellInfo.data?.has_attachments"
              :document-field="'attachment_supplierorder'"
              :form-data-source="{
                attachment_category: portalStore.attachmentCategoryEnum.ORDER,
              }"
              :entities-id="getAttachmentEntities(cellInfo.data, ['supplier_order', 'subject'])"
              :refresh-grid-async="() => refreshGridAsync()"
            />
          </template>
          <template #gridLayoutConfigTemplate>
            <grid-layout-config-template
              :grid-ext-ref="gridRef"
              :grid-ext-id="gridId"
            />
          </template>
          <template #main-info-caption-template="{ data }">
            <div
              class="dx-form-group-caption"
              style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
            >
              <span class="dx-form-group-caption">
                {{ i18n.t('common.mainInfo', 2, { caption: data.caption }) }}
              </span>
            </div>
          </template>
          <template #multiSelectFilterRef="{ data }">
            <multi-select-filter
              :ref="el => setMultiSelectFilterRef(data.component.option('field'), el)"
              :data-source="getFilterDataSource(data.component.option('field'))"
              :field="data.component.option('field')"
              :grid-ref="gridRef"
              :apply-filter="applyFilter"
              v-bind="{ data }"
            />
          </template>
          <template #supplierEditor="{ data: cellInfo }">
            <supplier-drop-down-box
              :disabled="!!cellInfo.row.data?.id"
              :data="cellInfo"
            />
          </template>
          <template #supplierDivisionEditor="{ data: cellInfo }">
            <supplier-division-drop-down-box
              :disabled="!!cellInfo.row.data?.id || currentSupplierId == null"
              :data="cellInfo"
              :supplier="currentSupplierId"
              show-clear-button
            />
          </template>
          <template #depositEditor="{ data: cellInfo }">
            <deposit-drop-down-box
              :data="cellInfo"
              :disabled="entityLock?.isReadOnly.value"
            />
          </template>
          <template
            #editButtonsTemplate="{ data: cellInfo }"
          >
            <edit-lock-buttons-template
              :entity-name="'SupplierOrder'"
              :cell-info="cellInfo"
              :grid="gridRef"
              :entity-lock="entityLock"
              :is-locked-available="true"
              :buttons="rowButtons"
            />
          </template>
          <template #customToolbarAddButtonTemplate>
            <custom-toolbar-add-button-template
              :grid="gridRef"
              :entity-lock="entityLock"
            />
          </template>
          <template #customToolbarDeleteButtonTemplate>
            <custom-toolbar-delete-button-template
              :selected-rows-id="selectedRows?.map(i => i.id) || []"
              :grid="gridRef"
              :entity-lock="entityLock"
              :on-delete="batchDeleteAsync"
              :disabled="selectedRows.length === 0"
            />
          </template>
        </dx-data-grid>
      </dx-item>
      <dx-item
        :collapsible="true"
        min-size="50%"
      >
        <div style="height: 100%; width: 100%;">
          <supplier-order-row-grid
            v-if="selectedDocument"
            :data="selectedDocument"
            :entity-lock="entityLock"
            @row-saved="onRowSaved"
            @content-ready="onDetailContentReady"
          />
          <div
            v-else
            style="min-height: 1px;"
          />
        </div>
      </dx-item>
    </dx-splitter>
  </div>
</template>
<style scoped lang="scss">
</style>
